import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchUserPermissions } from '../../services/authService';

// Async thunk for fetching user permissions
export const fetchPermissions = createAsyncThunk(
    'permissions/fetchPermissions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchUserPermissions();
            return response.data.data; // Extract the permissions array
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    permissions: [],
    loading: false,
    error: null,
    permissionMap: {},
};

const permissionsSlice = createSlice({
    name: 'permissions',
    initialState,
    reducers: {
        clearPermissions: (state) => {
            state.permissions = [];
            state.permissionMap = {};
            state.error = null;
            state.loading = false;
        },
        clearPermissionsError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPermissions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPermissions.fulfilled, (state, action) => {
                state.loading = false;
                state.permissions = action.payload;
                state.error = null;

                // Create a permission map for quick lookup
                state.permissionMap = action.payload.reduce((map, perm) => {
                    map[perm.permission] = true;
                    return map;
                }, {});
            })
            .addCase(fetchPermissions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.permissions = [];
                state.permissionMap = {};
            });
    },
});

// Selectors
export const selectPermissions = (state) => state.permissions.permissions;
export const selectPermissionsLoading = (state) => state.permissions.loading;
export const selectPermissionsError = (state) => state.permissions.error;
export const selectPermissionMap = (state) => state.permissions.permissionMap;

// Helper selector to check if user has a specific permission
export const selectHasPermission = (permissionName) => (state) => {
    return Boolean(state.permissions.permissionMap[permissionName]);
};

// Helper selector to check multiple permissions at once
export const selectHasAnyPermission = (permissionNames) => (state) => {
    return permissionNames.some(permission =>
        Boolean(state.permissions.permissionMap[permission])
    );
};

// Helper selector to check if user has all permissions
export const selectHasAllPermissions = (permissionNames) => (state) => {
    return permissionNames.every(permission =>
        Boolean(state.permissions.permissionMap[permission])
    );
};

export const { clearPermissions, clearPermissionsError } = permissionsSlice.actions;
export default permissionsSlice.reducer;
